import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, AccountBalance, Payment } from "@mui/icons-material";
import * as React from "react";
import { useState } from "react";
import {
  invoiceCheckout as invoiceCheckoutApi,
  invoiceParentCheckout as invoiceParentCheckoutApi,
} from "apis";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "store";
import { AuthState } from "store/features/auth";

interface Props {
  open: boolean;
  onClose: () => void;
  id: number;
}

// Only EDAHABIA is exposed for now. CIB Visa/Mastercard via Chargily
// will be added back as a second option once that integration is
// verified end-to-end. Keep value="1" so the dialog → backend mapping
// stays consistent (PaymentService maps "1" → EDAHABIA).
const paymentMethods = [
  {
    value: "1",
    label: "EDAHABIA",
    icon: <AccountBalance />,
    description: "Pay with Algérie Poste card",
  },
];

const InvoicePayoutDialog: React.FC<Props> = ({ open, onClose, id }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selected, setSelected] = useState("1");
  const [processing, setProcessing] = useState(false);
  const { role } = useSelector<RootState, AuthState>((store) => store.auth);

  const onClickPayBtn = () => {
    setProcessing(true);
    const checkoutFn =
      role === "NURSERY_ADMIN" ? invoiceCheckoutApi : invoiceParentCheckoutApi;
    checkoutFn({ id, type: selected })
      .then(({ res }: { res: string }) => {
        onClose();
        window.location.href = res;
      })
      .catch(() => setProcessing(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Payment sx={{ fontSize: 24 }} />
          <Typography variant="h6" fontWeight={700}>
            {t("labels.payment")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{
            mb: 1.5,
            display: "block",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {t("labels.payment_,method")}
        </Typography>

        <Box display="flex" flexDirection="column" gap={1.5}>
          {paymentMethods.map((method) => {
            const isSelected = selected === method.value;
            return (
              <Box
                key={method.value}
                onClick={() => setSelected(method.value)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: 2.5,
                  border: 2,
                  borderColor: isSelected ? "primary.main" : "divider",
                  bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.04)
                    : "transparent",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: isSelected ? "primary.main" : "primary.light",
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: isSelected
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.text.primary, 0.06),
                    color: isSelected ? "primary.main" : "text.secondary",
                    transition: "all 0.2s",
                  }}
                >
                  {method.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body1"
                    fontWeight={isSelected ? 700 : 500}
                  >
                    {method.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {method.description}
                  </Typography>
                </Box>
                {/* Radio indicator */}
                <Box
                  sx={{
                    ml: "auto",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: 2,
                    borderColor: isSelected ? "primary.main" : "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {isSelected && (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      {/* Actions */}
      <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={onClickPayBtn}
          disabled={processing}
          startIcon={
            processing ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Payment />
            )
          }
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { boxShadow: 2 },
          }}
        >
          {processing ? t("labels.pending") : t("buttons.pay")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default InvoicePayoutDialog;
